/* eslint-disable max-len */
/* eslint-disable react/no-array-index-key */
import { useState, useCallback, useEffect } from 'react';
import YearFilter from 'components/Globals/SlideFilter';
import { rankingResponsiveSettings } from 'components/Globals/SlideFilter/responsiveSettings';
import Link from 'components/Globals/Base/Link';
import type { CompanyRankingList } from 'api/fragments';
import useBreakpoint from 'hooks/useBreakpoint';
import Placeholder from 'components/Placeholder';
import Dropdown from 'components/Globals/Base/Dropdown';
import SectionTitle from 'components/Globals/SectionTitle';
import Tabs from 'components/Globals/Tabs';
import getCompanyPageTestId from 'constants/testsIds/companyPage';
import { RankingItemsWrapper, Wrapper } from './InRankingsStyles';
import CompanyRank from './CompanyRank';

export interface Props {
  companyName: string;
  rankings: CompanyRankingList[];
  companySlug: string;
  previewKey?: string;
}

const InRankings = ({ companyName, rankings, companySlug, previewKey }: Props) => {
  const isMobile = useBreakpoint('mdMax');
  const [dropdownExpanded, setDropdownExpanded] = useState(false);

  const shiftF500AndG500Tabs = (list: string[]) => {
    const fortuneIndex = list.indexOf('Fortune 500');
    const globalIndex = list.indexOf('Global 500');

    if (fortuneIndex !== -1) {
      list.splice(fortuneIndex, 1);
      list.unshift('Fortune 500');
      if (globalIndex !== -1) {
        list.splice(globalIndex, 1);
        list.splice(1, 0, 'Global 500');
      }
    } else if (globalIndex !== -1) {
      list.splice(globalIndex, 1);
      list.unshift('Global 500');
    }

    return [...new Set(list)];
  };

  const uniqueRankings = shiftF500AndG500Tabs([...new Set(rankings.map((rank) => rank.title))]);

  const selectedRank = rankings.filter((rank) => rank.title === uniqueRankings[0]);
  const [rank, setRank] = useState(selectedRank);
  const [selectedOption, setSelectedOption] = useState(uniqueRankings[0]);

  const rankYears = rank.map(({ year }) => year.toString());
  const [selectedRankYears, setSelectedRankYears] = useState(rankYears);
  const [selectedYear, setSelectedYear] = useState(selectedRankYears[0]);

  const [ranks, setRanks] = useState<{ rank: string; title: string; slug: string }[]>([]);
  const [showSkeleton, setShowSkeleton] = useState(true);

  const handleTabClick = (index: number) => {
    setShowSkeleton(true);
    const newRank = rankings.filter((ranking) => ranking.title === uniqueRankings[index]);
    if (newRank.length > 0) {
      setRank(newRank);
      const newRankYears = newRank.map(({ year }) => year.toString());

      if (newRankYears.length > 0) {
        setSelectedRankYears(newRankYears);
        setSelectedYear(newRankYears[0]);
        setShowSkeleton(false);
      }
    }
  };

  const fetchRanksList = useCallback(
    async (year: string, list: CompanyRankingList | undefined) => {
      const rankingUri = list?.slug.replace('/ranking/', '');
      try {
        const response = await fetch(
          `/api/getCompanyListPreview/${companySlug}/${rankingUri}/${year}/?previewKey=${previewKey}`,
        );
        const responseData = (await response.json()) as { rank: string; title: string; slug: string }[];
        setRanks(responseData);
        setShowSkeleton(false);
      } catch {
        setRanks([]);
        setShowSkeleton(false);
      }
    },
    [companySlug],
  );

  const changeYear = (year: string) => {
    setShowSkeleton(true);
    setSelectedYear(year);
    const ranking = rank.find((selection: CompanyRankingList) => selection.year === Number(year));
    if (!ranking) return;
    // eslint-disable-next-line no-void
    void fetchRanksList(year, ranking);
  };

  const yearList = rank
    .filter((rankItem) => rankItem.description !== null && selectedRankYears.includes(rankItem.year.toString()))
    .map((selRank) => selRank.year.toString());

  useEffect(() => {
    // eslint-disable-next-line no-void
    void fetchRanksList(selectedYear, rank[0]);
    // We want to fetch the list of rankings only the first time the component is mounted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rank[0].title]);

  const link = rank.find((selection) => selection.year === Number(selectedYear))?.permalink;

  const handleDropdownClick = () => {
    setDropdownExpanded((prev) => !prev);
  };

  const handleDropdownOptionClick = (value: string, index: number) => {
    const newRank = rankings.filter((ranking) => ranking.title === value);
    if (newRank.length > 0) {
      setRank(newRank);
      const newRankYears = newRank.map(({ year }) => year.toString());
      if (newRankYears.length > 0) {
        setSelectedRankYears(newRankYears);
        setSelectedYear(newRankYears[0]);
      }
    }
    setDropdownExpanded(false);
    setSelectedOption(uniqueRankings[index]);
  };

  const skeletons = new Array(5).fill(undefined);

  const tabsConfig = uniqueRankings.map((ranking: string) => ({
    children: (
      <>
        <YearFilter
          dataCy={getCompanyPageTestId('YEAR_BUTTON')}
          itemClassName='year ranking'
          className='year-rank-filter'
          valueList={yearList}
          availableValues={selectedRankYears}
          responsiveSettings={rankingResponsiveSettings}
          selectedValue={selectedYear}
          changeSelectedValue={changeYear}
        />
        <RankingItemsWrapper>
          {showSkeleton
            ? skeletons.map((_, index) => (
                <Placeholder
                  key={index}
                  size={{ height: 45, maxWidth: 1123 }}
                />
              ))
            : ranks.length > 0 &&
              ranks.map(({ rank: value, title, slug }, index) => (
                <CompanyRank
                  slug={slug}
                  rank={value}
                  name={title}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${title}-${rank}-${index}`}
                  active={title === companyName}
                />
              ))}
        </RankingItemsWrapper>
        <hr />
        {link && (
          <Link
            className='link-button'
            type='primaryButton'
            size={{ default: 'large' }}
            href={link}
            dataCy={getCompanyPageTestId('FORTUNE_RANKINGS_FULL_LIST_BTN')}
            aria-label='View the full ranking list'
          >
            View the Full List
          </Link>
        )}
      </>
    ),
    title: ranking,
  }));

  return (
    <Wrapper data-cy={getCompanyPageTestId('COMPANY_PANEL')}>
      <SectionTitle
        text={`${companyName} in Fortune Rankings`}
        tag='h2'
        variant='border'
        titleDataCy={getCompanyPageTestId('FORTUNE_RANKINGS_TITLE')}
      />
      {isMobile ? (
        <>
          <Dropdown
            isExpanded={dropdownExpanded}
            onBtnClick={handleDropdownClick}
            onOptionClick={handleDropdownOptionClick}
            list={uniqueRankings}
            selected={selectedOption}
            size='small'
            dataCy={getCompanyPageTestId('COMPANY_RANKINGS_DROPDOWN')}
          />
          <YearFilter
            itemClassName='year ranking'
            className='year-rank-filter'
            valueList={yearList}
            availableValues={selectedRankYears}
            responsiveSettings={rankingResponsiveSettings}
            selectedValue={selectedYear}
            changeSelectedValue={changeYear}
            dataCy={getCompanyPageTestId('YEAR_BUTTON')}
          />
          <RankingItemsWrapper data-cy={getCompanyPageTestId('RANKING_ITEMS_WRAPPER')}>
            {showSkeleton
              ? skeletons.map((_, index) => (
                  <Placeholder
                    key={index}
                    size={{ height: 45, maxWidth: 1123 }}
                  />
                ))
              : ranks.length > 0 &&
                ranks.map(({ rank: value, title, slug }, index) => (
                  <CompanyRank
                    slug={slug}
                    rank={value}
                    name={title}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${title}-${rank}-${index}`}
                    active={title === companyName}
                    dataCy={getCompanyPageTestId('COMPANY_RANK_WRAPPER')}
                  />
                ))}
          </RankingItemsWrapper>
          <hr />
          {link && (
            <Link
              type='primaryButton'
              href={link}
              aria-label='View the Full List'
              dataCy={getCompanyPageTestId('FORTUNE_RANKINGS_FULL_LIST_BTN')}
            >
              View the Full List
            </Link>
          )}
        </>
      ) : (
        <Tabs
          defaultIndex={0}
          config={tabsConfig}
          onClick={handleTabClick}
          enableTabsSlider
          tabDataCy={getCompanyPageTestId('COMPANY_IN_FORTUNE_RANKINGS')}
        />
      )}
    </Wrapper>
  );
};

export default InRankings;
